import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { IHttpResponse } from '@partners/api';
import { Observable } from 'rxjs';
import { IHttpAction, IHttpOptions } from '../model';
import { HTTP_CONFIG } from '../tokens/http.tokens';

@Injectable({
  providedIn: 'root'
})
export class HttpService {
  private _http = inject(HttpClient);
  private _config = inject(HTTP_CONFIG);

  constructor() {}

  private buildApiUrl({ endpoint, options }: IHttpAction): URL {
    return this._config.buildRequestEndpoint({ endpoint, options });
  }

  /**
   * a socialized variation of http "get" method. it only loads textual resources over
   * using http calls, meaning the response is free text (i.e - not of type IHttpResponse)
   * it also accepts partial url, generally we want to use it for dynamically loading any
   * string resource (such as svg content or dynamic texts from config file)
   * @param endpoint a partial url of the (local) resource
   * @param options headers, context etc'
   * @returns an observable of text (string) type
   */
  // fetchText<T, R extends string>(endpoint: string, options?: IHttpOptions): Observable<string> {
  //   options = this.getOptions(options);
  //   const url = options?.useRelativeUrl ? endpoint : this.buildApiUrl({ endpoint, options }).href;
  //   return this._http.get(url, { responseType: 'text' }) as Observable<string>;
  // }

  fetch<T, R>(endpoint: string, options?: IHttpOptions): Observable<IHttpResponse<R>> {
    options = this.getOptions(options);
    const { href } = this.buildApiUrl({ endpoint, options });
    return this._http.get(href, options) as Observable<IHttpResponse<R>>;
  }

  post<T, R>(endpoint: string, model?: T, options?: IHttpOptions): Observable<IHttpResponse<R>> {
    options = this.getOptions(options);
    const { href } = this.buildApiUrl({ endpoint, options });
    return this._http.post(href, model, options) as Observable<IHttpResponse<R>>;
  }

  put<T, R>(endpoint: string, model?: T, options?: IHttpOptions): Observable<IHttpResponse<R>> {
    options = this.getOptions(options);
    const { href } = this.buildApiUrl({ endpoint, options });
    return this._http.put(href, model, options) as Observable<IHttpResponse<R>>;
  }

  delete<T, R>(endpoint: string, options?: IHttpOptions): Observable<IHttpResponse<R>> {
    options = this.getOptions(options);
    const { href } = this.buildApiUrl({ endpoint, options });
    return this._http.delete(href, options) as Observable<IHttpResponse<R>>;
  }

  /**
   * @description if headers param has value, we will override the current header property or add a new one. return the updated headers collection.
   * @param headers
   * @returns new HttpHeaders
   */
  private getHeaders(headers?: HttpHeaders): HttpHeaders {
    let httpHeaders: HttpHeaders = this._config.httpHeaders;
    const hasHeaders = headers?.keys()?.length;
    // TODO: replace this with something like => httpHeaders.set(headers)
    if (hasHeaders > 0) {
      headers.keys().forEach((key) => {
        httpHeaders = httpHeaders.append(key, headers.get(key));
      });
    }

    return httpHeaders;
  }

  private getParams<T>(params: any) {
    return new HttpParams({ fromObject: params });
  }

  /**
   * @description get optional parameters such as params, headers and context and return HTTP options
   * @param options
   * @returns http options or {}
   */
  private getOptions(options: IHttpOptions = {}) {
    return {
      ...options,
      ...{
        params: this.getParams(options?.params),
        headers: this.getHeaders(options?.headers),
        context: options?.context
      }
    };
  }
}
